import { splitEncoding } from '@contentful/live-preview';
import type { Options } from '@contentful/rich-text-react-renderer';
import type {
  Block,
  Inline,
  Text as ContentfulText,
} from '@contentful/rich-text-types';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import type { CardOpts } from '@/cms/Card';
import { CMSSection } from '@/cms/CMSSection';
import { ConditionalComponent } from '@/cms/ConditionalComponent/ConditionalComponent';
import { NavQuickLinksWithBanner } from '@/cms/NavQuickLinksWithBanner/NavQuickLinksWithBanner';
import { Breadcrumb, type BreadcrumbProps } from '@/components/Breadcrumb';
import type { FaqWrapProps } from '@/components/Cms/FaqWrap';
import type { IframeProps } from '@/components/Cms/Iframe/Iframe';
import LatestArticles from '@/components/Cms/LatestArticles/LatestArticles';
import { Container } from '@/components/Container';
import type { HeroProductListingOpts } from '@/components/HeroProductListing/types';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { List } from '@/components/List';
import { Typography } from '@/components/Typography';
import type {
  ContentfulBannerFragment,
  ContentfulBreadcrumbFragment,
  ContentfulCardFragment,
  ContentfulConditionalComponentContentCollection,
  ContentfulConditionalComponentFragment,
  ContentfulContactUsFragment,
  ContentfulContentGroupFragment,
  ContentfulEventFragment,
  ContentfulExhibitionListFragment,
  ContentfulFaqCategoriesFragment,
  ContentfulFindAStoreFragment,
  ContentfulFreeTextContentFragment,
  ContentfulGridContentCollection,
  ContentfulGridFragment,
  ContentfulHeadingFragment,
  ContentfulHeroProductListingFragment,
  ContentfulHeroSliderFragment,
  ContentfulImageWithFocalPointFragment,
  ContentfulLiveStoryFragment,
  ContentfulNavQuickLinksFragment,
  ContentfulNavQuickLinksWithBannerFragment,
  ContentfulPageBreadcrumbFragment,
  ContentfulPlaceholderCheckoutPaymentsFragment,
  ContentfulPlaceholderServiceFragment,
  ContentfulPlaceholderStoresFragment,
  ContentfulProductCardFragment,
  ContentfulServicesFragment,
  ContentfulSliderProductsManualFragment,
} from '@/graphql/contentful/api';
import type {
  CollectionCardsQuery,
  LanguageCodeEnum,
  ProductCardsQuery,
} from '@/graphql/saleor/api';
import { useContactInfo } from '@/hooks/useContactInfo';
import {
  getFilterReleasedProducts,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import type { ProductHit } from '@/integrations/algolia/types';
import {
  fetchCollectionData,
  fetchProductData,
} from '@/integrations/saleor/api';
import {
  CARDS_IMAGE_SIZE,
  ENVIRONMENT_NAME,
  NEXT_PUBLIC_IMAGES_FORMAT,
} from '@/lib/const';
import { skusToSlugs } from '@/lib/product';
import { DEFAULT_COUNTRY } from '@/lib/regions';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe } from '@/lib/types';

import type {
  ContentfulData,
  ContentfulPageBreadcrumb,
  ContentType,
  HeroProductListingType,
  SaleorType,
  SectionsOpts,
} from './types';

/**
 * Dynamically load large sized non-global CMS components.
 * All CMS components need Server Side Rendering
 */
const ExhibitionList = dynamic(
  () =>
    import('@/cms/ExhibitionList/ExhibitionList').then(
      (mod) => mod.ExhibitionList,
    ),
  { ssr: true },
);
const ContentGroup = dynamic(
  () =>
    import('@/cms/ContentGroup/ContentGroup').then((mod) => mod.ContentGroup),
  { ssr: true },
);
const NavQuickLinks = dynamic(
  () =>
    import('@/cms/NavQuickLinks/NavQuickLinks').then(
      (mod) => mod.NavQuickLinks,
    ),
  { ssr: true },
);
const PlaceholderHeader = dynamic(
  () =>
    import('@/components/Header/Placeholder').then(
      (mod) => mod.PlaceholderHeader,
    ),
  { ssr: true },
);
const PlaceholderEvent = dynamic(
  () =>
    import('@/cms/Event/PlaceholderEvent').then((mod) => mod.PlaceholderEvent),
  { ssr: true },
);
const PlaceholderService = dynamic(
  () =>
    import('@/cms/PlaceholderService').then((mod) => mod.PlaceholderService),
  { ssr: true },
);
const HeroSlider = dynamic(
  () => import('@/cms/HeroSlider').then((mod) => mod.HeroSlider),
  { ssr: true },
);
const Stores = dynamic(() => import('@/cms/Stores').then((mod) => mod.Stores), {
  ssr: true,
});
const Services = dynamic(
  () => import('@/cms/Services').then((mod) => mod.Services),
  {
    ssr: true,
  },
);
const FindAStoreComponent = dynamic(
  () =>
    import('@/components/FindAStore').then((mod) => mod.FindAStoreComponent),
  {
    ssr: true,
  },
);
const Card = dynamic(() => import('@/cms/Card').then((mod) => mod.Card), {
  ssr: true,
});
const MediaWrapper = dynamic(
  () => import('@/cms/MediaWrapper').then((mod) => mod.MediaWrapper),
  {
    ssr: true,
  },
);
const Banner = dynamic(() => import('@/cms/Banner').then((mod) => mod.Banner), {
  ssr: true,
});
const Heading = dynamic(
  () => import('@/cms/Heading/Heading').then((mod) => mod.Heading),
  {
    ssr: true,
  },
);
const FaqCategories = dynamic(
  () =>
    import('@/cms/FaqCategories/FaqCategories').then(
      (mod) => mod.FaqCategories,
    ),
  {
    ssr: true,
  },
);
const FaqWrap = dynamic(
  () => import('@/cms/FaqWrap/FaqWrap').then((mod) => mod.FaqWrap),
  {
    ssr: true,
  },
);
const CheckoutPaymentsPlaceholder = dynamic(
  () =>
    import('@/cms/CheckoutPaymentsPlaceholder').then(
      (mod) => mod.CheckoutPaymentsPlaceholder,
    ),
  {
    ssr: true,
  },
);
const ContactUs = dynamic(
  () => import('@/cms/ContactUs/ContactUs').then((mod) => mod.ContactUs),
  {
    ssr: true,
  },
);
const FreeTextContent = dynamic(
  () => import('@/cms/FreeTextContent').then((mod) => mod.FreeTextContent),
  {
    ssr: true,
  },
);
const SliderProducts = dynamic(
  () => import('@/cms/SliderProducts').then((mod) => mod.SliderProducts),
  {
    ssr: true,
  },
);
const ProductCard = dynamic(
  () => import('@/cms/ProductCard').then((mod) => mod.ProductCard),
  {
    ssr: true,
  },
);
const Grid = dynamic(() => import('@/cms/Grid').then((mod) => mod.Grid), {
  ssr: true,
});
const Iframe = dynamic(
  () => import('@/cms/Iframe/Iframe').then((mod) => mod.Iframe),
  {
    ssr: true,
  },
);
const LiveStory = dynamic(
  () => import('@/cms/LiveStory/LiveStory').then((mod) => mod.LiveStory),
  {
    ssr: true,
  },
);

const cardsWithoutContainer = [
  'HeroBanner',
  'MediaAndBottomText',
  'MediaBanner',
  'GenericCountdownCard',
];

export const parseSaleorType = (
  saleorType: Maybe<string>,
): SaleorType | undefined => {
  const cleaned = saleorType ? splitEncoding(saleorType).cleaned : undefined;
  return cleaned ? JSON.parse(cleaned) : undefined;
};

/**
 * We need to additionally use loop indexes since in one page the same content
 * type can be used multiple times.
 */
export const generateSectionKey = (data: ContentfulData, index: number) =>
  `${data?.sys.id}-${index}`;

export const getOptsForType = <T = unknown>(
  type: ContentType,
  opts: SectionsOpts | undefined,
) => (opts?.filter((opt) => opt.type === type) ?? []) as SectionsOpts<T>;

export const getOptForType = <T = unknown>(
  type: ContentType,
  opts: SectionsOpts | undefined,
) => getOptsForType<T>(type, opts)?.[0]?.opts as T | undefined;

export type RenderContentfulSection<Opts extends SectionsOpts | undefined> =
  typeof renderContentfulSection<Opts>;

export const renderContentfulSection = <Opts extends SectionsOpts | undefined>(
  data: ContentfulData,
  sectionOpts?: Opts,
  extraDataRecord?: CmsExtraDataRecord,
  draftMode: boolean | undefined = false,
) => {
  let props = null;
  let opts = null;
  let cardProps = null;
  let navQuickLinksProps = null;
  const sysId = data.sys.id;
  const extraData = extraDataRecord ? extraDataRecord[sysId] : undefined;
  const typename = data?.__typename as ContentType;
  let navLinks = null;

  switch (typename) {
    case 'PlaceholderHeader':
      return <PlaceholderHeader />;
    case 'Event':
      props = data as ContentfulEventFragment;
      return (
        <CMSSection>
          <PlaceholderEvent {...props} />
        </CMSSection>
      );
    case 'PlaceholderService':
      props = data as ContentfulPlaceholderServiceFragment;
      return (
        <CMSSection {...generateDataTestId('IdentifyWatch', 'section')}>
          <PlaceholderService {...props} />
        </CMSSection>
      );
    case 'HeroSlider':
      props = data as ContentfulHeroSliderFragment;
      return <HeroSlider {...props} extraDataRecord={extraDataRecord} />;
    case 'PlaceholderStores':
      props = data as ContentfulPlaceholderStoresFragment;
      return <Stores {...props} />;
    case 'ServicesCollection':
      props = data as ContentfulServicesFragment;
      return <Services {...props} />;
    case 'NavQuickLinks':
      navLinks = data as ContentfulNavQuickLinksFragment;
      props = {
        ...navLinks,
        linksCollection: {
          ...navLinks.linksCollection,
          items: navLinks.linksCollection?.items?.filter((item) => item),
        },
      } as ContentfulNavQuickLinksFragment;
      return <NavQuickLinks {...props} key={sysId} />;
    case 'NavQuickLinksWithBanner':
      props = data as ContentfulNavQuickLinksWithBannerFragment;
      cardProps = props.card as ContentfulCardFragment;
      opts = getOptsForType<BreadcrumbProps>('Card', sectionOpts)[0];
      navQuickLinksProps = {
        ...props.navQuickLinks,
        linksCollection: {
          ...props.navQuickLinks?.linksCollection,
          items: props.navQuickLinks?.linksCollection?.items?.filter(
            (item) => item,
          ),
        },
      } as ContentfulNavQuickLinksFragment;
      return (
        <NavQuickLinksWithBanner>
          {cardsWithoutContainer?.includes(cardProps?.type as string) ? (
            <Card
              preload
              {...cardProps}
              breadcrumb={opts?.opts}
              extraData={extraData?.extraData}
            />
          ) : (
            <Container disableGutters maxWidth='xl'>
              <Card
                preload
                {...cardProps}
                breadcrumb={opts?.opts}
                extraData={extraData?.extraData}
              />
            </Container>
          )}
          <NavQuickLinks {...navQuickLinksProps} />
        </NavQuickLinksWithBanner>
      );
    case 'FindAStore':
      props = data as ContentfulFindAStoreFragment;
      return (
        <CMSSection>
          <Container>
            <FindAStoreComponent {...props} />
          </Container>
        </CMSSection>
      );
    case 'Card':
      props = data as ContentfulCardFragment;
      opts = getOptsForType<CardOpts>('Card', sectionOpts)[0];
      return (
        <CMSSection
          {...generateDataTestId('card', 'section')}
          withMargin={opts?.opts.withSectionMargin ?? true}
        >
          {cardsWithoutContainer?.includes(props.type as string) ? (
            <Card
              preload
              {...props}
              breadcrumb={opts?.opts}
              extraData={extraData?.extraData}
            />
          ) : (
            <Container disableGutters maxWidth='xl'>
              <Card
                preload
                {...props}
                breadcrumb={opts?.opts}
                extraData={extraData?.extraData}
              />
            </Container>
          )}
        </CMSSection>
      );
    case 'ImageWithFocalPoint':
      props = data as ContentfulImageWithFocalPointFragment;
      return (
        <CMSSection {...generateDataTestId('mediaWrapper', 'section')}>
          <Container disableGutters maxWidth='xl'>
            <MediaWrapper preload {...props} />
          </Container>
        </CMSSection>
      );

    case 'Banner':
      props = data as ContentfulBannerFragment;
      return <Banner {...props} />;
    case 'ContentGroup':
      props = data as ContentfulContentGroupFragment;
      if (props.type === 'LatestNews') {
        return (
          <CMSSection data-test-id='sectionLatestNews'>
            <Container>
              <LatestArticles />
            </Container>
          </CMSSection>
        );
      }
      return <ContentGroup {...props} extraDataRecord={extraDataRecord} />;
    case 'Heading':
      props = data as ContentfulHeadingFragment;
      return <Heading Heading={props}></Heading>;
    case 'ExhibitionList':
      props = data as ContentfulExhibitionListFragment;
      return <ExhibitionList {...props} />;
    case 'FaqCategories':
      props = data as ContentfulFaqCategoriesFragment;
      return <FaqCategories {...props} />;
    case 'FaqWrap':
      props = data as FaqWrapProps;
      return <FaqWrap {...props} />;
    case 'PlaceholderCheckoutPayments':
      props = data as ContentfulPlaceholderCheckoutPaymentsFragment;
      return <CheckoutPaymentsPlaceholder {...props} />;
    case 'ContactUs':
      props = data as ContentfulContactUsFragment;
      return <ContactUs data-test-id='ContactUs' {...props}></ContactUs>;
    case 'FreeTextContent':
      props = data as ContentfulFreeTextContentFragment;
      return (
        <CMSSection>
          <Container>
            <FreeTextContent {...props} />
          </Container>
        </CMSSection>
      );
    case 'SliderProductsManual':
      props = data as ContentfulSliderProductsManualFragment;
      return (
        <CMSSection {...generateDataTestId('carouselManual', 'section')}>
          <SliderProducts
            {...props}
            draftMode={draftMode}
            productData={extraData?.extraData?.productData}
          />
        </CMSSection>
      );
    case 'ProductCard':
      props = data as ContentfulProductCardFragment;
      return (
        <ProductCard
          autoLoadCTA={true}
          {...props}
          productData={extraData?.extraData?.productData[0]}
        />
      );
    case 'Breadcrumb':
      props = data as ContentfulBreadcrumbFragment;
      opts = getOptsForType<BreadcrumbProps>('Breadcrumb', sectionOpts)[0];
      return (
        <CMSSection withMargin={false}>
          <Breadcrumb.Wrapper>
            <Breadcrumb
              inverted={props.style === 'Light'}
              items={opts?.opts?.items}
            />
          </Breadcrumb.Wrapper>
        </CMSSection>
      );
    case 'Grid':
      props = data as ContentfulGridFragment;
      return (
        <CMSSection {...generateDataTestId('grid', 'section')}>
          <Grid
            {...props}
            draftMode={draftMode}
            extraDataRecord={extraDataRecord}
          />
        </CMSSection>
      );

    case 'Iframe':
      props = data as IframeProps;
      return (
        <CMSSection data-testid='iframeSection'>
          <Iframe {...props} />
        </CMSSection>
      );

    case 'LiveStoryContentType':
      props = data as ContentfulLiveStoryFragment;
      return <LiveStory {...props} />;

    default:
      return null;
  }
};

export const StyleSpan = styled.span`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p2};
    font-family: ${theme.fontFamilies.openSans};
    line-height: ${theme.lineHeights.p2};
    padding: ${theme.spacing(2, 0)};
  `}
`;

export const StyleSpanAlertBanner = styled.span`
  ${({ theme }) => css`
    font-size: inherit;
    font-family: ${theme.fontFamilies.openSans};
  `}
`;

export const ReplaceContactInfoFnWrapper = ({
  node,
}: {
  node: Block | Inline;
}) => {
  const contactInfo = useContactInfo();

  return <ReplaceContactInfo contactInfo={contactInfo} node={node} />;
};

const ReplaceContactInfo = ({
  contactInfo,
  node,
}: {
  contactInfo: {
    email: string;
    phoneNumber: string;
    weekdayHours: string;
    weekendHours: string;
  };
  node: Block | Inline;
}) => {
  const combinedTextElements: ReactNode[] = [];

  node.content?.forEach((child, index) => {
    if ('value' in child && (child as ContentfulText).value) {
      const textValue = (child as ContentfulText).value;

      const parts = textValue.split(
        /(%{{contact.phone}}%|%{{contact.email}}%|https?:\/\/[^\s]+)/g,
      );

      parts.forEach((part, i) => {
        if (part === '%{{contact.phone}}%') {
          combinedTextElements.push(
            <a
              key={`phone-${index}-${i}`}
              href={`tel:${contactInfo.phoneNumber}`}
            >
              <strong>{contactInfo.phoneNumber}</strong>
            </a>,
          );
        } else if (part === '%{{contact.email}}%') {
          combinedTextElements.push(
            <a key={`email-${index}-${i}`} href={`mailto:${contactInfo.email}`}>
              <strong>{contactInfo.email}</strong>
            </a>,
          );
        } else if (/https?:\/\/[^\s]+/.test(part)) {
          combinedTextElements.push(
            <a
              key={`link-${index}-${i}`}
              href={part}
              rel='noopener noreferrer'
              target='_blank'
            >
              {part}
            </a>,
          );
        } else {
          combinedTextElements.push(
            <StyleSpan key={`text-${index}-${i}`}>{part}</StyleSpan>,
          );
        }
      });
    } else if (child.nodeType === INLINES.HYPERLINK) {
      const { uri } = child.data;
      const isExternalLink =
        uri.startsWith('http://') || uri.startsWith('https://');

      const textNode = child.content[0] as ContentfulText;

      combinedTextElements.push(
        <a
          key={`hyperlink-${index}`}
          href={uri}
          rel={isExternalLink ? 'noopener noreferrer' : undefined}
          target={isExternalLink ? '_blank' : '_self'}
        >
          {textNode?.value}
        </a>,
      );
    }
  });

  return <Typography scale='p2'>{combinedTextElements}</Typography>;
};

export const COMPONENT_RENDERER_OPTIONS: Options = {
  renderNode: {
    [INLINES.HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
      const { uri } = node.data;
      const isExternalLink =
        uri.startsWith('http://') || uri.startsWith('https://');
      return (
        <LinkWithLocale
          href={node.data.uri}
          rel={isExternalLink ? 'noopener noreferrer' : undefined}
          target={isExternalLink ? '_blank' : '_self'}
        >
          {children}
        </LinkWithLocale>
      );
    },
    [BLOCKS.UL_LIST]: (node, children) => <List>{children}</List>,
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => <strong>{children}</strong>,
    [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: ReactNode) => {
      const textValue = node.content
        ?.map((child) =>
          'value' in child ? (child as unknown as ContentfulText).value : '',
        )
        .join('');

      if (
        textValue?.includes('%{{contact.phone}}%') ||
        textValue?.includes('%{{contact.email}}%') ||
        textValue?.includes('%{{contact.translation}}')
      ) {
        return <ReplaceContactInfoFnWrapper node={node} />;
      }

      if (textValue?.trim()) {
        return (
          <Typography fontFamily='openSans' pb={2} pt={2} scale='p2'>
            {children}
          </Typography>
        );
      }

      return null;
    },
    [BLOCKS.HEADING_2]: (node, children) => (
      <Typography fontFamily='italianPlate' pb={2} pt={2} scale='h4'>
        {children}
      </Typography>
    ),
  },
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
  },
};

export type CollectionData = NonNullable<
  CollectionCardsQuery['collections']
>['edges'][number]['node'];

export type ProductData = NonNullable<
  ProductCardsQuery['products']
>['edges'][number]['node'] & {
  launch_date?: number;
};

export type CmsExtraDataRecord = Record<string, CmsExtraData>;

export type CmsExtraData = {
  extraData?: {
    collectionData: CollectionData[];
    productData: ProductData[];
  };
};

export class ContentfulDataWithExtraDataError extends Error {
  id?: string;
  constructor(message: string, id?: string) {
    super(message);
    this.id = id;
  }
}

/**
 * contentful saleor data only has ObjectId, need fetch product data on frontend
 */
export const fetchContentfulExtraData = async (
  contentFulDataArr: ContentfulData[],
  languageCode: LanguageCodeEnum,
  channel: string,
  draftMode: boolean | undefined = false,
) => {
  const promiseList: Promise<
    CmsExtraData & {
      id: string;
    }
  >[] = parse(contentFulDataArr, languageCode, channel);
  const formatData = await Promise.allSettled(promiseList);
  const record: CmsExtraDataRecord = {};
  for (const item of formatData) {
    if (item.status === 'rejected') {
      record[item?.reason?.value?.id] = {
        extraData: {
          collectionData: [],
          productData: [],
        },
      };
    } else {
      if (item.value.extraData) {
        const productData = item.value.extraData.productData;
        const indexName = `${ENVIRONMENT_NAME}.PRODUCT.${channel}`;

        const releasedProductHits = await getFilterReleasedProducts<ProductHit>(
          productData.map((item) => item.defaultVariant?.sku ?? item.slug),
          '',
          undefined,
          ProductConfigurationFilterType.COMPONENTS,
          indexName,
          true,
          draftMode,
        );
        const releasedSlugs = releasedProductHits.map((hit) => hit.objectID);
        const hitsByObjectId = new Map(
          releasedProductHits.map((hit) => [hit.objectID, hit]),
        );
        const releasedProducts = productData
          .filter(
            (item) =>
              item.defaultVariant?.sku &&
              releasedSlugs?.includes(item.defaultVariant?.sku),
          )
          .map((item) => {
            const hit = hitsByObjectId.get(item.defaultVariant?.sku ?? '');
            return {
              ...item,
              launch_date: hit && 'launch_date' in hit ? hit.launch_date : 0,
            };
          });

        record[item?.value?.id] = {
          extraData: {
            collectionData: item.value.extraData.collectionData,
            productData: releasedProducts,
          },
        };
      }
    }
  }
  return record;
};
type ContentFulDataArr = (ContentfulData | null | undefined)[];
const filterUnsupportedContentItems = (
  items?: ReadonlyArray<Maybe<{ __typename?: string }>>,
): ContentFulDataArr =>
  (items ?? []).filter((item): item is ContentfulData => {
    if (!item) {
      return false;
    }

    return item.__typename !== 'CollectionTeaser';
  });

const parse = (
  contentFulDataArr: ContentFulDataArr,
  languageCode: LanguageCodeEnum,
  channel: string,
): Promise<
  CmsExtraData & {
    id: string;
  }
>[] => {
  const promiseList: Promise<
    CmsExtraData & {
      id: string;
    }
  >[] = [];

  for (let i = 0; i < contentFulDataArr.length; i++) {
    const contentfulData = contentFulDataArr[i];
    if (!contentfulData) {
      continue;
    }
    const typename = contentfulData?.__typename as ContentType;
    if (typename === 'HeroSlider') {
      const data = contentfulData as ContentfulHeroSliderFragment;
      promiseList.push(
        ...parse(
          filterUnsupportedContentItems(data.slidesCollection?.items),
          languageCode,
          channel,
        ),
      );
    } else if (typename === 'ContentGroup') {
      const data = contentfulData as ContentfulContentGroupFragment;
      promiseList.push(
        ...parse(
          filterUnsupportedContentItems(data.itemsCollection?.items),
          languageCode,
          channel,
        ),
      );
    } else if (typename === 'ConditionalComponent') {
      const data = contentfulData as ContentfulConditionalComponentFragment;
      promiseList.push(
        ...parse(
          filterUnsupportedContentItems(
            (
              data.contentCollection as ContentfulConditionalComponentContentCollection
            )?.items,
          ),
          languageCode,
          channel,
        ),
      );
    } else if (typename === 'Grid') {
      const data = contentfulData as ContentfulGridFragment;
      const { items } =
        (data.contentCollection as ContentfulGridContentCollection) || {
          items: [],
        };
      promiseList.push(
        ...parse(filterUnsupportedContentItems(items), languageCode, channel),
      );
    } else {
      const params = getFetchProductDataParams(contentfulData);
      if (params.saleorTypes.length === 0 && params.skus.length === 0) {
        continue;
      }
      promiseList.push(
        new Promise((resolve, reject) => {
          fetchSaleorData(
            params.saleorTypes,
            params.skus,
            languageCode,
            channel,
          )
            .then((extraData) => {
              resolve({
                id: contentfulData.sys.id,
                extraData: {
                  collectionData: extraData?.collectionData || [],
                  productData: extraData?.productData || [],
                },
              });
            })
            .catch((e) => {
              reject(
                new ContentfulDataWithExtraDataError(e, contentfulData.sys.id),
              );
            });
        }),
      );
    }
  }
  return promiseList;
};

const getFetchProductDataParams = (
  contentfulData: ContentfulData,
): { saleorTypes: Maybe<string>[]; skus: Maybe<string>[] } => {
  const typename = contentfulData?.__typename as ContentType;
  if (typename === 'SliderProductsManual') {
    const data = contentfulData as ContentfulSliderProductsManualFragment;
    const skus = (data.products ?? []).map(
      (s) => splitEncoding(s ?? '').cleaned,
    );
    return { saleorTypes: [], skus };
  } else if (typename === 'ProductCard') {
    const data = contentfulData as ContentfulProductCardFragment;
    const sku = splitEncoding(data.product ?? '').cleaned || null;
    return { saleorTypes: [], skus: sku ? [sku] : [] };
  } else {
    return { saleorTypes: [], skus: [] };
  }
};

const fetchSaleorData = async (
  saleorTypes: Array<string | null | undefined>,
  skus: Array<string | null | undefined>,
  languageCode: LanguageCodeEnum,
  channel: string,
) => {
  if (saleorTypes.length > 0 || skus?.length > 0) {
    let parsedSaleorTypes: SaleorType[];

    try {
      parsedSaleorTypes =
        saleorTypes?.map((i) => (i ? JSON.parse(i) : undefined)) || [];
    } catch (e) {
      // is ObjectID Array , not saleor Product/Collection arr
      parsedSaleorTypes =
        (saleorTypes?.map((i) => {
          return {
            Channel: channel,
            ID: i,
            Type: 'Product',
          };
        }) as SaleorType[]) || [];
    }

    const collectionTypes = parsedSaleorTypes.filter(
      (type) => type?.Type === 'Collection',
    ) as SaleorType[];
    const productTypes = parsedSaleorTypes.filter(
      (type) => type?.Type === 'Product',
    ) as SaleorType[];

    const skusParams = (skus?.filter((sku) => !!sku) as string[]) || [];

    const [collectionData, productData, productDataBySkus] = await Promise.all([
      collectionTypes.length
        ? fetchCollectionData({
            languageCode: languageCode,
            filter: {
              ids: collectionTypes.map(({ ID }) => ID),
            },
            channel: collectionTypes[0].Channel || DEFAULT_COUNTRY.channelSlug,
          })
        : [],
      productTypes.length
        ? fetchProductData({
            languageCode: languageCode,
            thumbnailSize: CARDS_IMAGE_SIZE,
            imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
            filter: {
              ids: productTypes.map(({ ID }) => ID),
            },
            first: productTypes.length,
            channel: productTypes[0].Channel || DEFAULT_COUNTRY.channelSlug,
          })
        : [],
      skusParams && skusParams.length
        ? fetchProductData({
            languageCode: languageCode,
            thumbnailSize: CARDS_IMAGE_SIZE,
            imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
            filter: {
              slugs: skusToSlugs(skusParams),
            },
            first: skusParams.length,
            channel: channel || DEFAULT_COUNTRY.channelSlug,
          })
        : [],
    ]);
    return {
      collectionData,
      productData: [...productData, ...productDataBySkus],
    };
  }
  return {
    collectionData: [],
    productData: [],
  };
};

export const renderContentfulFixedPage = <
  Opts extends SectionsOpts | undefined,
>(
  data: ContentfulData,
  sectionOpts?: Opts,
  extraDataRecord?: CmsExtraDataRecord,
  isNotEmpty?: boolean,
  isAuthenticated?: boolean | null,
) => {
  const section = renderContentfulSection(data, sectionOpts, extraDataRecord);
  if (section) {
    return section;
  }

  let props = null;
  const typename = data?.__typename as ContentType;

  switch (typename) {
    case 'ConditionalComponent':
      props = data as ContentfulConditionalComponentFragment;
      return (
        <ConditionalComponent
          {...props}
          extraDataRecord={extraDataRecord}
          isAuthenticated={isAuthenticated}
          isNotEmpty={isNotEmpty}
        />
      );
    default:
      return null;
  }
};

export const handleDuplicateBreadcrumbs = (
  breadcrumbs: Maybe<ContentfulPageBreadcrumb[]>,
): ContentfulPageBreadcrumbFragment[] =>
  breadcrumbs?.reduce((arr: ContentfulPageBreadcrumbFragment[], current) => {
    if (current?.slug && !arr.some((item) => item.slug === current.slug)) {
      arr.push(current);
    }
    return arr;
  }, []) ?? [];

export function isContentfulCardFragment(
  data: any,
): data is ContentfulCardFragment {
  return data?.__typename === 'Card';
}

export const getHeroProductListingType = (
  props: ContentfulHeroProductListingFragment,
  opts?: HeroProductListingOpts,
  pageName?: string,
) => {
  if (!opts) {
    return (props.type as HeroProductListingType) ?? 'editorial';
  }
  if (opts.type === 'watch editorial page' && pageName === 'collections') {
    return opts.type;
  }
  // when HeroProductListing type is editorial, not use hrdcode in cms opts
  return props.type === 'editorial' ? 'editorial' : opts.type;
};
